import { useId, type ReactNode } from 'react';
import { Button } from '../atoms/Button';
import { Heading } from '../atoms/Heading';
import { Icon } from '../atoms/Icon';
import { useModalDialog } from '../hooks/useModalDialog';
import { useSheetDrag } from '../hooks/useSheetDrag';
import styles from './Drawer.module.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Buttons pinned to the bottom, e.g. cancel and save. */
  actions?: ReactNode;
  /** Accessible label of the close button (i18n). */
  closeLabel: string;
  size?: 'md' | 'lg';
  children?: ReactNode;
}

/**
 * Side panel for longer forms: slides in from the right on wide screens and
 * up as a sheet on phones. Based on the native modal `<dialog>` (focus trap,
 * Escape, inert background).
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  actions,
  closeLabel,
  size = 'md',
  children,
}: DrawerProps) {
  const ref = useModalDialog(open);
  const drag = useSheetDrag(ref, onClose);
  const titleId = useId();
  const descriptionId = useId();

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      tabIndex={-1}
      className={`${styles.drawer} ${styles[size]}`}
      aria-labelledby={titleId}
      {...(description ? { 'aria-describedby': descriptionId } : {})}
      onCancel={(e) => {
        e.preventDefault();
        // A sheet opened from a form dialog must not close that one, too.
        e.stopPropagation();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.panel}>
        <span className={styles.handle} aria-hidden="true" {...drag} />
        <header className={styles.header} {...drag}>
          <div className={styles.titles}>
            <Heading level={2} size="lg" id={titleId}>
              {title}
            </Heading>
            {description && (
              <div id={descriptionId} className={styles.description}>
                {description}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label={closeLabel}
            onClick={onClose}
            iconStart={<Icon name="close" />}
          />
        </header>
        <div className={styles.body}>{children}</div>
        {actions && <footer className={styles.actions}>{actions}</footer>}
      </div>
    </dialog>
  );
}
